/**
 * Fees — Admin landing for the fee lifecycle (#17 invoice slice).
 *
 * URL-driven tabs (mirrors CompliancePage):
 *   - "View History"  (default, no ?tab) → per-agreement / per-month billing history
 *     + invoice lifecycle (<FeeHistorySection />). First in line + landing page
 *     (Pedro 2026-06-25): the overview operators reach for most.
 *   - "Upload 20th AuM" (?tab=aum-20th)   → the per-account 20th-of-month AuM entry,
 *     the fee basis (<AccountAumSection /> from PortfolioDataPage).
 *   - "Compute Month" (?tab=compute-month) → the firm-wide compute/freeze runner
 *     (the exported <BatchRunner /> from FeeRunBatchPage).
 *   - "Import invoices" (?tab=import)     → bulk accountant-Excel + PDF import
 *     (<InvoiceImportSection />).
 *   - "Fee Schedules" (?tab=fee-schedules) → two inner sub-tabs (Fee Schedule
 *     model, C-244): "Standard Schedule" (<StandardEditor />, gated
 *     fee_schedule.standard.manage) and "Custom Fee Schedules"
 *     (<CustomFeeSchedulesSection />, gated fee_schedule.custom.create —
 *     define/edit + basis-group + availability; DIRECT Admin write, the
 *     maker-checker path was retired with the Fee Schedule model).
 */
import { Container, Group, Stack, Tabs, Text, Title } from '@mantine/core';
import { IconAddressBook, IconCalculator, IconChartLine, IconClipboardCheck, IconFileImport, IconFileUpload, IconHistory, IconReceipt2, IconScale, IconSettings } from '@tabler/icons-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { BatchRunner } from './FeeRunBatchPage';
import { StandardEditor } from './StandardFeeSchedulePage';
import { AccountAumSection } from './PortfolioDataPage';
import { AdhocFeeSection } from '../components/AdhocFeeSection';
import { CustomFeeSchedulesSection } from '../components/CustomFeeSchedulesSection';
import { FeeHistorySection } from '../components/FeeHistorySection';
import { FeeHistoryUploadSection } from '../components/FeeHistoryUploadSection';
import { StoredDataPeekSection } from '../components/StoredDataPeekSection';
import { FirmWideBillingSection } from '../components/FirmWideBillingSection';
import { InvoiceImportSection } from '../components/InvoiceImportSection';
import { BackToCompliance } from '../components/BackToCompliance';
import { RequireAccess } from '../components/RequireAccess';
import { useDirtyForm } from '../components/DirtyFormProvider';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useCurrentUser } from '../hooks/useAuth';
import { ADMIN_PAGE_READ_ROLES } from '../api/types';

export function FeesPage() {
  useDocumentTitle('Fees');
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirmDiscardAsync } = useDirtyForm();
  const urlTab = searchParams.get('tab') ?? 'history';
  // One-shot seed for the Compute tab: the 20th-AuM upload hand-off pre-selects the just-uploaded
  // month; BatchRunner consumes it on mount (setSeedComputeMonth(undefined)) so a normal visit is blank.
  const [seedComputeMonth, setSeedComputeMonth] = useState<string | undefined>();
  // Tab switches change only the ?tab query, not the pathname — so the app's
  // centralized useBlocker (pathname-scoped, DirtyFormProvider) does NOT catch
  // them, and keepMounted={false} would silently drop a dirty subtab's staged
  // edits. Guard the switch through confirmDiscardAsync (a no-op when nothing
  // is dirty; prompts otherwise).
  const setActiveTab = (v: string | null) => {
    if (!v) return;
    void confirmDiscardAsync().then((ok) => {
      if (!ok) return;
      const next = new URLSearchParams(searchParams);
      if (v === 'history') next.delete('tab'); else next.set('tab', v);
      setSearchParams(next, { replace: true });
    });
  };

  // P45 (Pedro 2026-10-15): RM and Compliance read History only (invoice PDFs included);
  // every other tab is Admin's (hidden for them, and refused by the server).
  const { user } = useCurrentUser();
  const isAdmin = user?.role === 'admin';

  if (user && !isAdmin) {
    return (
      <RequireAccess roles={ADMIN_PAGE_READ_ROLES}>
        <Container size="xl">
          <Stack gap="lg">
            <BackToCompliance to="/" label="Back to Welcome" />
            <div>
              <Group gap="xs"><IconHistory size={22} /><Title order={2}>Fees history</Title></Group>
              <Text c="dimmed">
                Every agreement&apos;s computed fees by month, with the invoices issued and settled.
              </Text>
            </div>
            <FeeHistorySection readOnly />
          </Stack>
        </Container>
      </RequireAccess>
    );
  }

  return (
    <RequireAccess roles={['admin']}>
      <Container size="xl">
        <Stack gap="lg">
          <BackToCompliance to="/admin" label="Back to Admin" />
          <div>
            <Group gap="xs"><IconCalculator size={22} /><Title order={2}>Fees</Title></Group>
            <Text c="dimmed">
              The monthly fee cycle — upload the 20th-of-month AuM, compute, import invoices,
              and settle in history — plus historical fee upload, fee schedules and billing details.
            </Text>
          </div>

          <Tabs value={urlTab} onChange={setActiveTab} keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="aum-20th" leftSection={<IconChartLine size={14} />}>
                1 &middot; Upload month
              </Tabs.Tab>
              <Tabs.Tab value="compute-month" leftSection={<IconCalculator size={14} />}>
                2 &middot; Compute month
              </Tabs.Tab>
              <Tabs.Tab value="import" leftSection={<IconFileImport size={14} />}>
                3 &middot; Import invoices
              </Tabs.Tab>
              <Tabs.Tab value="history" leftSection={<IconHistory size={14} />}>
                4 &middot; History
              </Tabs.Tab>
              <Tabs.Tab value="adhoc" leftSection={<IconReceipt2 size={14} />}>
                Ad-hoc fee
              </Tabs.Tab>
              <Tabs.Tab value="fee-history" leftSection={<IconFileUpload size={14} />}>
                Upload history
              </Tabs.Tab>
              <Tabs.Tab value="fee-calc-check" leftSection={<IconClipboardCheck size={14} />}>
                Calc Check
              </Tabs.Tab>
              <Tabs.Tab value="setup" leftSection={<IconSettings size={14} />}>
                Setup
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="aum-20th" pt="md">
              {/* P31: the date scope moved out of the tab label into the panel. */}
              <Text size="sm" c="dimmed" mb="sm">
                Months from January 2026 onward (fees computed in the app). Earlier months: Upload history.
              </Text>
              <AccountAumSection onGoToCompute={(m) => { setSeedComputeMonth(m); setActiveTab('compute-month'); }} />
            </Tabs.Panel>
            <Tabs.Panel value="compute-month" pt="md">
              <BatchRunner seedMonth={seedComputeMonth} onSeedConsumed={() => setSeedComputeMonth(undefined)} />
            </Tabs.Panel>
            <Tabs.Panel value="import" pt="md">
              <InvoiceImportSection />
            </Tabs.Panel>
            <Tabs.Panel value="history" pt="md">
              <FeeHistorySection />
            </Tabs.Panel>
            <Tabs.Panel value="adhoc" pt="md">
              <AdhocFeeSection />
            </Tabs.Panel>
            <Tabs.Panel value="fee-history" pt="md">
              <FeeHistoryUploadSection />
            </Tabs.Panel>
            <Tabs.Panel value="fee-calc-check" pt="md">
              <StoredDataPeekSection />
            </Tabs.Panel>
            <Tabs.Panel value="setup" pt="md">
              <SetupTabs />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Container>
    </RequireAccess>
  );
}

/** "Setup" groups the two low-traffic configuration surfaces (Pedro 2026-07-11) — the Fee
 *  Schedules editor and firm-wide Billing details — behind one tab, so the day-to-day
 *  monthly-cycle tabs (Upload · Compute · Import · History) read as the primary workflow.
 *  ?setup= selects the sub-tab (default fee-schedules). */
function SetupTabs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirmDiscardAsync } = useDirtyForm();
  const inner = searchParams.get('setup') ?? 'fee-schedules';
  const setInner = (v: string | null) => {
    if (!v) return;
    void confirmDiscardAsync().then((ok) => {
      if (!ok) return;
      const next = new URLSearchParams(searchParams);
      if (v === 'fee-schedules') next.delete('setup'); else next.set('setup', v);
      setSearchParams(next, { replace: true });
    });
  };
  return (
    <Tabs value={inner} onChange={setInner} keepMounted={false}>
      <Tabs.List>
        <Tabs.Tab value="fee-schedules" leftSection={<IconScale size={14} />}>Fee Schedules</Tabs.Tab>
        <Tabs.Tab value="billing-details" leftSection={<IconAddressBook size={14} />}>Billing details</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="fee-schedules" pt="md">
        <FeeSchedulesTabs />
      </Tabs.Panel>
      <Tabs.Panel value="billing-details" pt="md">
        <FirmWideBillingSection />
      </Tabs.Panel>
    </Tabs>
  );
}

/** The "Fee Schedules" tab's own two inner sub-tabs (D16 terminology):
 *  Standard Schedule (the firm default) and Custom Fee Schedules (named,
 *  agreement-owned, shared via availability). Both Admin-only, same as the
 *  rest of this page — each additionally gated on its own permission since
 *  they're independent capabilities (a future role could hold one without
 *  the other). */
function FeeSchedulesTabs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirmDiscardAsync } = useDirtyForm();
  const inner = searchParams.get('schedule') ?? 'standard';
  // Same query-param-not-pathname gap as the outer tabs — guard the sub-tab
  // switch so a dirty Custom-schedule Edit transaction isn't dropped silently.
  const setInner = (v: string | null) => {
    if (!v) return;
    void confirmDiscardAsync().then((ok) => {
      if (!ok) return;
      const next = new URLSearchParams(searchParams);
      if (v === 'standard') next.delete('schedule'); else next.set('schedule', v);
      setSearchParams(next, { replace: true });
    });
  };
  return (
    <Tabs value={inner} onChange={setInner} keepMounted={false}>
      <Tabs.List>
        <Tabs.Tab value="standard">Standard Schedule</Tabs.Tab>
        <Tabs.Tab value="custom">Custom Fee Schedules</Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="standard" pt="md">
        <RequireAccess permission="fee_schedule.standard.manage">
          <StandardEditor />
        </RequireAccess>
      </Tabs.Panel>
      <Tabs.Panel value="custom" pt="md">
        <RequireAccess permission="fee_schedule.custom.create">
          <CustomFeeSchedulesSection />
        </RequireAccess>
      </Tabs.Panel>
    </Tabs>
  );
}
